/**
 * Address verification API client
 * Requires authentication (sends Keycloak bearer token).
 */
import keycloakApi from './keycloak-auth';

export interface AddressInput {
  streetAddress: string;
  secondaryAddress?: string | null;
  city: string;
  state: string;
  zipCode: string;
  /** Pass with accountId to persist verification to account_verifications */
  partyId?: string | null;
  /** Pass with partyId to persist verification to account_verifications */
  accountId?: string | null;
}

export interface StandardizedAddress {
  streetAddress: string;
  secondaryAddress?: string | null;
  city: string;
  state: string;
  zipCode: string;
  zipPlus4?: string | null;
}

export interface AddressVerificationResponse {
  success: boolean;
  verified: boolean;
  hasCorrections: boolean;
  originalAddress: AddressInput;
  suggestedAddress?: StandardizedAddress | null;
  corrections?: string[];
  dpvConfirmation?: string | null;
  isResidential?: boolean | null;
  isVacant?: boolean | null;
  error?: {
    code: string;
    message: string;
  } | null;
  validationErrors?: Record<string, string[]> | null;
}

/**
 * Verify an address using USPS Address API.
 * Include partyId and accountId when available so the result is saved to account_verifications.
 * Sends the current user's Keycloak bearer token.
 */
export async function verifyAddress(address: AddressInput): Promise<AddressVerificationResponse> {
  const body: Record<string, unknown> = {
    streetAddress: address.streetAddress,
    secondaryAddress: address.secondaryAddress ?? null,
    city: address.city,
    state: address.state,
    zipCode: address.zipCode,
  };
  if (address.partyId) body.partyId = address.partyId;
  if (address.accountId) body.accountId = address.accountId;

  try {
    return await keycloakApi.authenticatedApiCall<AddressVerificationResponse>('/address/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network error occurred';
    const isUnauthorized =
      message.includes('401') || message.toLowerCase().includes('unauthorized');
    return {
      success: false,
      verified: false,
      hasCorrections: false,
      originalAddress: address,
      error: {
        code: isUnauthorized ? 'UNAUTHORIZED' : 'NETWORK_ERROR',
        message,
      },
    };
  }
}
